import { Fragment, useCallback, useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router-dom";

import { UnitTypes } from "constant";
import { useMapContext } from "context/MapContextProvider";
import features from "features";
import useShiftMap from "hooks/useShiftMap";
import { RootStateInterface } from "reducer";
import { MapLayerInterface } from "scenes/LayersManager/interfaces";
import { MarkerInterface } from "scenes/MapPage/interfaces";
import { MapStateInterface } from "../../interfaces";

import DynamicMarker from "./DynamicMarker/DynamicMarker";

interface MapMarkersInterface {
  markers: MarkerInterface[];
  activeMarkerId: string;
  opacityByLayerList: MapLayerInterface[];
  isClustering?: boolean;
}

const MapMarkers = ({
  markers,
  activeMarkerId,
  opacityByLayerList,
  isClustering
}: MapMarkersInterface) => {
  const dispatch = useDispatch();
  const map = useMap();
  const { id: projectId } = useParams();
  const { shiftMapPointDetails } = useShiftMap();

  const { mapMode } = useSelector<RootStateInterface, MapStateInterface>(
    (state) => state.map
  );

  const [draggableId, setDraggableId] = useState(null);
  const [viewBounds, setViewBounds] = useState(map.getBounds());

  const {
    openContextMenu,
    closeContextMenu,
    setPointCoordinates,
    pointCoordinates
  } = useMapContext();

  const clearDraggableData = useCallback(() => {
    setDraggableId(null);
    setPointCoordinates(null);
  }, [setPointCoordinates]);

  const onCancelDragging = useCallback(() => {
    clearDraggableData();
  }, [clearDraggableData]);

  const onAcceptDragging = useCallback(() => {
    let markerData = markers.find((marker) => marker.id === draggableId);

    const updatedMarkerData = {
      ...markerData,
      geo_data: {
        ...markerData.geo_data,
        coordinates: [pointCoordinates.lat, pointCoordinates.lng]
      }
    };

    dispatch(
      features.unitsManager.actions.updateUnitRequest({
        params: {
          projectId,
          unitId: draggableId,
          type: UnitTypes.POINT,
          alias: markerData.layerAlias
        },
        fields: updatedMarkerData,
        onSuccess: () => {
          clearDraggableData();
          toastr.success(`Done`, `Point updated`);
        }
      })
    );
  }, [
    clearDraggableData,
    dispatch,
    draggableId,
    markers,
    projectId,
    pointCoordinates
  ]);

  const onMarkerClick = useCallback(
    (marker) => () => {
      dispatch(
        features.unitsManager.actions.fetchUnitRequest({
          params: { projectId, alias: marker.layerAlias, id: marker.id }
        })
      );

      shiftMapPointDetails(marker.geo_data.coordinates);
    },
    [dispatch, projectId, shiftMapPointDetails]
  );

  const onMoveMarker = useCallback(
    (marker) => () => {
      setDraggableId(marker.id);
      setPointCoordinates({
        lat: marker.geo_data.coordinates[0],
        lng: marker.geo_data.coordinates[1]
      });
      closeContextMenu();
    },
    [closeContextMenu, setPointCoordinates]
  );

  const onDragEnd = (e) => {
    const { lat, lng } = e.target.getLatLng();
    setPointCoordinates({ lat, lng });
  };

  const isVisible = useCallback(
    (coordinates) => viewBounds && viewBounds.intersects([coordinates]),
    [viewBounds]
  );

  const getCoordinatesToCheck = (marker) =>
    draggableId === marker.id ? pointCoordinates : marker.geo_data.coordinates;

  useEffect(() => {
    const handleMove = () => {
      setViewBounds(map.getBounds());
    };

    map.on("moveend", handleMove);
    return () => {
      map.off("moveend", handleMove);
    };
  }, [map]);

  if (!markers.length) return null;

  return (
    <>
      {markers.map((marker: MarkerInterface, index: number) => (
        <Fragment key={`marker-${index}`}>
          {isVisible(getCoordinatesToCheck(marker)) && (
            <DynamicMarker
              marker={marker}
              index={index}
              draggableId={draggableId}
              pointCoordinates={pointCoordinates}
              activeMarkerId={activeMarkerId}
              opacityByLayerList={opacityByLayerList}
              mapMode={mapMode}
              onAcceptDragging={onAcceptDragging}
              onCancelDragging={onCancelDragging}
              onMarkerClick={onMarkerClick}
              onDragEnd={onDragEnd}
              openContextMenu={openContextMenu}
              onMoveMarker={onMoveMarker}
              isClustering={isClustering}
            />
          )}
        </Fragment>
      ))}
    </>
  );
};
export default MapMarkers;
